/**
 * `lib/peopleNextChange.ts` — #2867, #2933: the "Next change" of the People
 * list. Every dated change after today for one person, from these sources:
 * a posting that starts or ends, a rotate out, a join, and a level or FTE
 * move. `nextChanges` returns them all in date order, so the row can show
 * the first one with its icon and "+N" for the rest; `nextChangeText` is the
 * first one's text.
 *
 * Pure: no React, no fetching. Dates are ISO `YYYY-MM-DD` strings.
 */
import { fteWords } from "./fteWords";

export interface NextChangePosting {
  station: string;
  valid_from: string;
  valid_to_exclusive: string | null;
}

export interface NextChangeInput {
  joinedAt: string | null | undefined;
  leftAt: string | null | undefined;
  postings: readonly NextChangePosting[];
  /** Level moves ahead from the manpower plan: the date and the new level's label. */
  levelMoves: readonly { date: string; label: string }[];
  /** #2933 — FTE moves ahead from the manpower plan: the date and the new FTE. */
  fteMoves?: readonly { date: string; fte: number }[];
  today: string;
}

/** What kind of change: the row picks its icon by this. */
export type NextChangeKind =
  | "join"
  | "rotate_out"
  | "level"
  | "fte"
  | "posting_start"
  | "posting_end";

export interface NextChange {
  /** The day the change takes effect (sort key). */
  date: string;
  kind: NextChangeKind;
  text: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "1 Jan" from an ISO date. */
export function shortDayMonth(iso: string): string {
  const day = Number(iso.slice(8, 10));
  const month = MONTHS[Number(iso.slice(5, 7)) - 1] ?? "";
  return `${day} ${month}`;
}

/** The ISO day before `iso` (a posting's last day from its exclusive end). */
export function dayBefore(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** True when the posting covers `day`. */
export function postingCovers(p: NextChangePosting, day: string): boolean {
  return p.valid_from <= day && (p.valid_to_exclusive === null || p.valid_to_exclusive > day);
}

/** Same-day order: a rotate out first, a posting start last. */
const RANK: Record<NextChangeKind, number> = {
  rotate_out: 0,
  level: 1,
  fte: 1,
  join: 2,
  posting_end: 3,
  posting_start: 4,
};

/** Every change after today, earliest first. */
export function nextChanges(input: NextChangeInput): NextChange[] {
  const { today } = input;
  const found: NextChange[] = [];
  if (input.leftAt && input.leftAt > today) {
    found.push({
      date: input.leftAt,
      kind: "rotate_out",
      text: `Rotates out ${shortDayMonth(input.leftAt)}`,
    });
  }
  for (const move of input.levelMoves) {
    if (move.date > today) {
      found.push({
        date: move.date,
        kind: "level",
        text: `${move.label} from ${shortDayMonth(move.date)}`,
      });
    }
  }
  for (const move of input.fteMoves ?? []) {
    if (move.date > today) {
      found.push({
        date: move.date,
        kind: "fte",
        text: `${fteWords(move.fte)} from ${shortDayMonth(move.date)}`,
      });
    }
  }
  if (input.joinedAt && input.joinedAt > today) {
    found.push({
      date: input.joinedAt,
      kind: "join",
      text: `Joins ${shortDayMonth(input.joinedAt)}`,
    });
  }
  const starts = new Set(input.postings.map((p) => p.valid_from));
  for (const p of input.postings) {
    if (p.valid_from > today) {
      found.push({
        date: p.valid_from,
        kind: "posting_start",
        text: `${p.station} from ${shortDayMonth(p.valid_from)}`,
      });
    }
    const end = p.valid_to_exclusive;
    if (end && end > today) {
      const lastDay = dayBefore(end);
      // A posting that hands over to the next one, or ends as the person
      // rotates out, is not a change of its own.
      const handsOver = starts.has(end);
      // A last day already past says nothing about a later end, so only a
      // rotate out still ahead hides it.
      const endsWithLeave = !!input.leftAt && input.leftAt > today && lastDay >= input.leftAt;
      if (!handsOver && !endsWithLeave) {
        found.push({
          date: end,
          kind: "posting_end",
          text: `${p.station} ends ${shortDayMonth(lastDay)}`,
        });
      }
    }
  }
  found.sort((a, b) =>
    a.date === b.date ? RANK[a.kind] - RANK[b.kind] : a.date < b.date ? -1 : 1,
  );
  return found;
}

/** The text for the earliest change after today, or `null` when nothing is ahead. */
export function nextChangeText(input: NextChangeInput): string | null {
  return nextChanges(input)[0]?.text ?? null;
}
